// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING, ERROR_MESSAGES } from '@/api/constants';
import { getUserFacingErrorMessage } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { SupportSnackbar } from '@/components/support-snackbar';
import { UnsavedChangesDialog } from '@/components/unsaved-changes-dialog';
import { CHARGE_SCHEDULE } from '@/constants/charge-schedule';
import { INVOICE_MODE } from '@/constants/invoice-mode';
import { PRICING_MODE } from '@/constants/pricing-mode';
import { Currency } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { ItemPricing } from '@/items/api/types';
import { ProjectLineItem } from '@/project/api/types';
import { BillingPlanActivationSection } from '@/project/components/billing-plan-activation-section';
import { BillingPlanChargeListSection } from '@/project/components/billing-plan-charge-list-section';
import { BillingPlanChargeScheduleSection } from '@/project/components/billing-plan-charge-schedule-section';
import {
  BillingPlanFormValues,
  DRAWER_WIDTH,
  billingPlanFormSchema,
  computePeriodCount,
  computeShiftedRange,
  getDefaultValues,
  isValidFrequencyPair,
  milestoneGenerateBlockedReason,
} from '@/project/components/billing-plan-constants';
import { BillingPlanDrawerHeader } from '@/project/components/billing-plan-drawer-header';
import { BillingPlanDrawerSkeleton } from '@/project/components/billing-plan-drawer-skeleton';
import { BillingPlanInvoiceScheduleSection } from '@/project/components/billing-plan-invoice-schedule-section';
import { BillingPlanPricingSection } from '@/project/components/billing-plan-pricing-section';
import { BillingPlanReadinessSection } from '@/project/components/billing-plan-readiness-section';
import { useChargeScheduleState } from '@/project/hooks/use-charge-schedule-state';
import { useUpdateProjectLineItem } from '@/project/hooks/use-update-project-line-item';
import { buildLineItemUpdatePayload } from '@/project/utilities/build-line-item-update-payload';
import {
  findChargeAmountRange,
  findInvoiceAmountRange,
  sumChargeTotals,
} from '@/project/utilities/charge-amounts';
import { chargeDateKey } from '@/project/utilities/charge-date';
import { generateCharges } from '@/project/utilities/generate-charges';
import {
  INVOICED_CHARGE_LOCKED_TOOLTIP,
  TOTAL_OVERRIDE_CURRENCY_TOOLTIP,
  projectCurrencyLockedTooltip,
} from '@/project/utilities/project-currency';
import { MAX_DOLLAR_VALUE, MAX_DOLLAR_VALUE_LABEL } from '@/utilities/currency';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Drawer, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  lineItem: ProjectLineItem | null;
  defaultPricing?: ItemPricing | null;
  projectKey: string;
  // ISO the line item's currency is locked to because another project field
  // (contract value, discount, or a sibling line item) already established it.
  // Undefined when this line item may set the project currency freely.
  projectCurrencyLock?: IsoCurrencyCode;
  // When set, the matching charge row in the schedule list scrolls into view
  // and briefly highlights once the charges have loaded. Cleared via
  // onFocusChargeComplete after the highlight finishes.
  focusChargeKey?: string;
  onFocusChargeComplete?: () => void;
  // When true, the drawer renders a body of skeletons in place of the
  // form. Used by callers that fetch the line item lazily so the user
  // sees a loading affordance from the moment the drawer opens
  // instead of a flash of empty form fields.
  isLoading?: boolean;

  // Additional invalidations to fire when drawer form is updated
  additionalInvalidations?: string[][];
}

interface PeriodicScheduleFields {
  chargeFrequency: string;
  invoiceFrequency: string;
  invoiceMode: BillingPlanFormValues['invoiceMode'];
}

function activationDateKey(value: DateTime | null) {
  return value?.toISODate() ?? null;
}

function currencyKey(value: Currency | null): string | null {
  return value === null ? null : `${value.amount.toString()}:${value.isoCode}`;
}

export function BillingPlanDrawer(props: Props) {
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const { user } = useCurrentUser();
  const defaultCurrencyCode =
    props.projectCurrencyLock ?? user.organization.defaultCurrency;
  const defaultPricing = props.defaultPricing ?? null;
  const defaultValues = getDefaultValues(
    props.lineItem,
    defaultPricing,
    defaultCurrencyCode
  );

  const form = useForm<BillingPlanFormValues>({
    resolver: zodResolver(billingPlanFormSchema),
    reValidateMode: 'onSubmit',
    defaultValues,
  });
  const lastPeriodicScheduleRef = useRef<PeriodicScheduleFields>({
    chargeFrequency: defaultValues.chargeFrequency ?? 'MONTHLY',
    invoiceFrequency: defaultValues.invoiceFrequency ?? 'MONTHLY',
    invoiceMode: defaultValues.invoiceMode ?? INVOICE_MODE.ADVANCE,
  });

  const pricingMode = useWatch({ control: form.control, name: 'pricingMode' });
  const chargeSchedule = useWatch({
    control: form.control,
    name: 'chargeSchedule',
  });
  const chargeFrequency = useWatch({
    control: form.control,
    name: 'chargeFrequency',
  });
  const invoiceFrequency = useWatch({
    control: form.control,
    name: 'invoiceFrequency',
  });
  const invoiceMode = useWatch({ control: form.control, name: 'invoiceMode' });
  const price = useWatch({ control: form.control, name: 'price' });
  const totalOverride = useWatch({
    control: form.control,
    name: 'totalOverride',
  });
  const chargeMode = useWatch({ control: form.control, name: 'chargeMode' });
  const activationDate = useWatch({
    control: form.control,
    name: 'activationDate',
  });
  // `useWatch` subscribes reliably to nested entry changes (name and
  // percentage edits inside milestonePhases.entries.N.*). `form.watch` on the
  // parent path is inconsistent about re-rendering on deep-nested updates,
  // which leaves the generate-blocked-reason computation stale.
  const milestonePhases = useWatch({
    control: form.control,
    name: 'milestonePhases',
  });
  const quantity = props.lineItem?.quantity ?? 0;
  const lineItemStartOn = props.lineItem?.startOn ?? null;
  const lineItemEndOn = props.lineItem?.endOn ?? null;
  const hasDates = lineItemStartOn !== null && lineItemEndOn !== null;

  // Per-charge amount shown in the Pricing section. Fixed is fixed, per-unit
  // is quantity × unit price. Derived from the single form.price field,
  // interpreted per pricingMode. The separate unitPriceNum / fixedPriceNum
  // aliases feed generateCharges which still takes both as optional inputs.
  const priceAmount = price.amount;
  const priceDecimal =
    parseCurrencyAmount(priceAmount ?? '') ?? new CurrencyDecimal(0);
  const priceNum = priceDecimal.toNumber();
  const unitPriceNum = pricingMode === PRICING_MODE.PER_UNIT ? priceNum : 0;
  const fixedPriceNum = pricingMode === PRICING_MODE.FIXED ? priceNum : 0;
  const baseChargeAmount =
    pricingMode === PRICING_MODE.PER_UNIT
      ? priceDecimal.times(quantity)
      : priceDecimal;
  const totalOverrideAmount =
    totalOverride.amount.trim() !== ''
      ? parseCurrencyAmount(totalOverride.amount.trim())
      : null;
  const totalOverrideNum = totalOverrideAmount?.toNumber() ?? null;

  // Charge / invoice counts and totals are computed differently per schedule:
  //
  // PERIODIC: charge count = number of billing periods between start and end;
  //   invoice count = number of invoice periods; total money = per-charge
  //   amount × charges.
  //
  // MILESTONE: charge count = number of configured phases (one charge per
  //   phase). Invoice-date count mirrors whatever dates the user has set on
  //   the generated charges — each phase's expected_billable_date is
  //   user-driven (the cron skips MANUAL charges, so it won't stamp them),
  //   so the plan-side count echoes the charge-list count to keep the
  //   cross-check row always green. Total money is the plan's line-item
  //   total (total_override ?? fixed_price or unit_price × quantity).
  const isMilestoneMode = chargeSchedule === CHARGE_SCHEDULE.MILESTONE;
  const milestonePhaseCount = milestonePhases?.entries.length ?? 0;
  // PERIODIC charges anchor on activationDate (with the line item's
  // duration preserved by day-count). Counting periods over the shifted
  // [activationDate, activationDate + duration] range keeps the
  // readiness section in sync with the actual charge list — without
  // this, a month-anchored shift like Jan 31 → Feb 28 would skew the
  // plan-side count vs the generated charges.
  const shiftedRange = computeShiftedRange(
    lineItemStartOn,
    lineItemEndOn,
    activationDate
  );
  const numCharges = isMilestoneMode
    ? milestonePhaseCount
    : computePeriodCount(
        shiftedRange?.start ?? null,
        shiftedRange?.end ?? null,
        chargeFrequency
      );
  const periodicNumInvoices = isMilestoneMode
    ? 0
    : computePeriodCount(
        shiftedRange?.start ?? null,
        shiftedRange?.end ?? null,
        invoiceFrequency
      );
  // `numInvoices` feeds the invoice schedule section (periodic only,
  // hidden for milestone) and the `estInvoiceRange` math below. For milestone
  // the section is hidden and `estInvoiceRange` is unused, so 0 here is safe;
  // the real milestone invoice-date count is billingPlanInvoiceDateCount
  // derived lower in the render where localCharges is in scope.
  const numInvoices = periodicNumInvoices;

  const baseLineItemTotal = isMilestoneMode
    ? baseChargeAmount
    : baseChargeAmount.times(numCharges);
  // TotalOverride is a line-item total override for PERIODIC schedules only,
  // distributed across charges. MILESTONE plans cannot carry an override (the
  // BE forbids it), so they always use the pricing-derived total and split it
  // across phases by percentage.
  const lineItemTotal =
    totalOverrideAmount !== null && !isMilestoneMode
      ? totalOverrideAmount
      : baseLineItemTotal;
  const totalMoneyAcrossCharges = lineItemTotal;

  // The per-charge and per-invoice amounts are read off the charges the
  // current configuration would generate, so an uneven split shows the
  // amounts that will actually be billed rather than a fractional average.
  // The preview is always dated so its charges can be grouped by bill date.
  const previewCharges = useMemo(() => {
    if (isMilestoneMode || lineItemStartOn === null || lineItemEndOn === null) {
      return [];
    }

    return generateCharges({
      startOn: lineItemStartOn,
      endOn: lineItemEndOn,
      chargeFrequency: chargeFrequency ?? 'MONTHLY',
      invoiceFrequency: invoiceFrequency ?? 'MONTHLY',
      invoiceMode: invoiceMode ?? 'ADVANCE',
      pricingMode,
      quantity,
      unitPrice: unitPriceNum,
      fixedPrice: fixedPriceNum,
      totalOverride: totalOverrideNum,
      chargeSchedule: CHARGE_SCHEDULE.PERIODIC,
      chargeMode: 'CONFIRMED',
      activationDate: activationDate?.isValid
        ? activationDate
        : lineItemStartOn,
      milestonePhases: null,
      currencyCode: price.isoCode,
    });
  }, [
    isMilestoneMode,
    lineItemStartOn,
    lineItemEndOn,
    chargeFrequency,
    invoiceFrequency,
    invoiceMode,
    pricingMode,
    quantity,
    unitPriceNum,
    fixedPriceNum,
    totalOverrideNum,
    activationDate,
    price.isoCode,
  ]);
  const scheduleChargeRange = findChargeAmountRange(previewCharges) ?? {
    min: baseChargeAmount,
    max: baseChargeAmount,
  };
  const estInvoiceRange =
    numInvoices > 0 ? findInvoiceAmountRange(previewCharges) : null;

  const computedTotal = baseLineItemTotal;

  // Track initial form values for taint detection (actual change, not just touched)
  const initialFormValues = useRef(defaultValues);
  // Auto-set invoice frequency to ONCE when charge frequency is ONCE. Guard
  // on the current value so re-renders (e.g. from other form mutations)
  // don't re-fire setValue with the same payload and nudge RHF's internal
  // subscription bookkeeping for no reason.
  useEffect(() => {
    if (
      chargeFrequency === 'ONCE' &&
      form.getValues('invoiceFrequency') !== 'ONCE'
    ) {
      form.setValue('invoiceFrequency', 'ONCE');
    }
  }, [chargeFrequency, form]);

  // Keep the form coherent across schedule-type changes. Milestone plans have
  // no invoice schedule or charge frequency; pricing mode stays whatever the
  // user picked (PER_UNIT or FIXED). Flipping back to PERIODIC drops the
  // milestone phases so the form doesn't round-trip a stale blob the backend
  // would reject under a non-milestone schedule.
  useEffect(() => {
    if (chargeSchedule === CHARGE_SCHEDULE.MILESTONE) {
      const currentChargeFrequency = form.getValues('chargeFrequency');
      const currentInvoiceFrequency = form.getValues('invoiceFrequency');
      const currentInvoiceMode = form.getValues('invoiceMode');
      if (currentChargeFrequency !== null) {
        lastPeriodicScheduleRef.current.chargeFrequency =
          currentChargeFrequency;
      }
      if (currentInvoiceFrequency !== null) {
        lastPeriodicScheduleRef.current.invoiceFrequency =
          currentInvoiceFrequency;
      }
      if (currentInvoiceMode !== null) {
        lastPeriodicScheduleRef.current.invoiceMode = currentInvoiceMode;
      }
      if (form.getValues('invoiceFrequency') !== null) {
        form.setValue('invoiceFrequency', null);
      }
      if (form.getValues('invoiceMode') !== null) {
        form.setValue('invoiceMode', null);
      }
      if (form.getValues('chargeFrequency') !== null) {
        form.setValue('chargeFrequency', null);
      }
      // Milestone plans are always CONFIRMED with no activation anchor — the
      // BE rejects any other combination. Force the form back into shape so
      // a stale PENDING toggle from the prior PERIODIC config doesn't ride
      // through to submit.
      if (form.getValues('chargeMode') !== 'CONFIRMED') {
        form.setValue('chargeMode', 'CONFIRMED', { shouldDirty: true });
      }
      if (form.getValues('activationDate') !== null) {
        form.setValue('activationDate', null, { shouldDirty: true });
      }
      return;
    }
    if (form.getValues('chargeFrequency') === null) {
      form.setValue(
        'chargeFrequency',
        lastPeriodicScheduleRef.current.chargeFrequency
      );
    }
    if (form.getValues('invoiceFrequency') === null) {
      form.setValue(
        'invoiceFrequency',
        lastPeriodicScheduleRef.current.invoiceFrequency
      );
    }
    if (form.getValues('invoiceMode') === null) {
      form.setValue('invoiceMode', lastPeriodicScheduleRef.current.invoiceMode);
    }
    if (form.getValues('milestonePhases') !== null) {
      form.setValue('milestonePhases', null);
    }
    // Coming back to PERIODIC after the milestone forced CONFIRMED + null,
    // seed activationDate to the line item's startOn so the form lands
    // valid (PERIODIC + CONFIRMED requires activation_date). The user can
    // still edit it; this just removes the empty-input intermediate state.
    if (
      form.getValues('chargeMode') === 'CONFIRMED' &&
      form.getValues('activationDate') === null &&
      lineItemStartOn !== null
    ) {
      form.setValue('activationDate', lineItemStartOn, {
        shouldDirty: true,
      });
    }
  }, [chargeSchedule, lineItemStartOn, form]);

  // Reset form when the line item's persisted values change. Depends on the
  // specific fields so a refetch that returns updated server data propagates
  // to the form even when the key hasn't changed.
  // Guarded by isDirty so in-progress edits aren't wiped — the form only
  // resyncs when the user hasn't touched it (or has just saved, clearing
  // the dirty flag).
  const lineItemKey = props.lineItem?.key;
  const isDirty = form.formState.isDirty;
  const liPricingMode = props.lineItem?.pricingMode ?? null;
  // The currency fields parse to fresh Currency object references on every
  // fetch; depend on stable string keys so the resync effect only re-fires
  // when the underlying amount or currency actually changes.
  const liUnitPrice = currencyKey(props.lineItem?.unitPrice ?? null);
  const liFixedPrice = currencyKey(props.lineItem?.fixedPrice ?? null);
  // The form's single `price` field seeds from whichever stored column
  // matches the current pricing mode (see getDefaultValues). Depending on
  // both here ensures a server refetch propagates whichever column the
  // BE rewrote.
  const liChargeSchedule = props.lineItem?.chargeSchedule ?? null;
  const liChargeFrequency = props.lineItem?.chargeFrequency ?? null;
  const liInvoiceMode = props.lineItem?.invoiceMode ?? null;
  const liInvoiceFrequency = props.lineItem?.invoiceFrequency ?? null;
  const liTotalOverride = currencyKey(props.lineItem?.totalOverride ?? null);
  const liChargeMode = props.lineItem?.chargeMode ?? null;
  const liActivationDate = props.lineItem?.activationDate ?? null;
  const defaultPricingMode = defaultPricing?.pricingMode ?? null;
  const defaultUnitPrice = defaultPricing?.unitPrice ?? null;
  const defaultFixedPrice = defaultPricing?.fixedPrice ?? null;
  const defaultChargeSchedule = defaultPricing?.chargeSchedule ?? null;
  const defaultChargeFrequency = defaultPricing?.chargeFrequency ?? null;
  const defaultInvoiceMode = defaultPricing?.invoiceMode ?? null;
  const defaultInvoiceFrequency = defaultPricing?.invoiceFrequency ?? null;
  // SKU catalog default matching the currently selected pricing mode, surfaced
  // as a reference beneath the price input.
  const defaultPriceForMode =
    pricingMode === PRICING_MODE.FIXED ? defaultFixedPrice : defaultUnitPrice;
  useEffect(() => {
    if (isDirty) return;
    const defaults = getDefaultValues(
      props.lineItem,
      defaultPricing,
      defaultCurrencyCode
    );
    form.reset(defaults);
    lastPeriodicScheduleRef.current = {
      chargeFrequency: defaults.chargeFrequency ?? 'MONTHLY',
      invoiceFrequency: defaults.invoiceFrequency ?? 'MONTHLY',
      invoiceMode: defaults.invoiceMode ?? INVOICE_MODE.ADVANCE,
    };
    initialFormValues.current = defaults;
  }, [
    lineItemKey,
    liPricingMode,
    liUnitPrice,
    liFixedPrice,
    liChargeSchedule,
    liChargeFrequency,
    liInvoiceMode,
    liInvoiceFrequency,
    liTotalOverride,
    liChargeMode,
    liActivationDate,
    defaultPricingMode,
    defaultUnitPrice,
    defaultFixedPrice,
    defaultChargeSchedule,
    defaultChargeFrequency,
    defaultInvoiceMode,
    defaultInvoiceFrequency,
    isDirty,
    props.lineItem,
    defaultPricing,
    defaultCurrencyCode,
    form,
  ]);

  const chargeScheduleState = useChargeScheduleState({
    projectKey: props.projectKey,
    lineItemKey: lineItemKey ?? null,
    isOpen: props.isOpen,
    lineItemStartOn,
    lineItemEndOn,
    pricingMode,
    chargeSchedule,
    chargeFrequency,
    invoiceFrequency,
    invoiceMode,
    quantity,
    unitPriceNum,
    fixedPriceNum,
    totalOverrideNum,
    milestonePhases,
    chargeMode,
    activationDate,
    currencyCode: price.isoCode,
  });

  const {
    localCharges,
    chargesModified,
    chargesLoaded,
    isChargeEditing,
    generationKey,
    isConfigOutOfSync,
    unrepresentableCharge,
    setIsChargeEditing,
    handleGenerateCharges,
    handleUpdateCharge,
    handleDeleteCharge,
    handleSplitCharge,
    handleAddCharge,
  } = chargeScheduleState;

  // Inline bound error rendered beneath "Line item total". The check is
  // live (no save-attempted gate) so the user sees the warning as soon
  // as the field combination produces an out-of-range aggregate — same
  // semantics for a pre-existing localCharge that exceeds the cap.
  const boundErrorRef = useRef<HTMLSpanElement>(null);
  const boundError = useMemo<string | null>(() => {
    if (lineItemTotal.greaterThan(MAX_DOLLAR_VALUE)) {
      return `Line item total must not exceed ${MAX_DOLLAR_VALUE_LABEL}`;
    }

    // An override below the invoiced charges would leave a negative amount for the remaining charges, so the plan cannot be saved.
    if (totalOverrideAmount !== null && !isMilestoneMode) {
      const invoicedTotal = localCharges
        .filter((charge) => charge.status === 'INVOICED')
        .reduce(
          (sum, charge) =>
            sum.plus(new CurrencyDecimal(charge.totalPrice.amount)),
          new CurrencyDecimal(0)
        );
      if (totalOverrideAmount.lessThan(invoicedTotal)) {
        return ERROR_MESSAGES.project_line_item_override_below_invoiced;
      }
    }

    const offendingCharge = localCharges.find(
      (charge) => Number(charge.totalPrice) > MAX_DOLLAR_VALUE
    );
    if (offendingCharge) {
      return `Charge "${offendingCharge.label}" exceeds ${MAX_DOLLAR_VALUE_LABEL}. Edit or remove it before saving.`;
    }

    if (unrepresentableCharge !== null) {
      return `Charge "${unrepresentableCharge.charge.label}": ${unrepresentableCharge.message}. Edit or remove it before saving.`;
    }

    return null;
  }, [
    lineItemTotal,
    localCharges,
    unrepresentableCharge,
    totalOverrideAmount,
    isMilestoneMode,
  ]);

  const [isCrossCheckExpanded, setIsCrossCheckExpanded] = useState(false);

  // Reset drawer-local UI state on the transition from open → closed only.
  // Prevents a prior session's snackbar, unsaved-changes modal, or
  // expanded cross-check from leaking into the next open. The form and
  // chargeScheduleState own their own reset effects — the form resyncs
  // via the lineItem-sync effect below (when the drawer reopens with a
  // potentially different line item), and the charges hook resets when
  // isOpen flips off. We also reset the form to defaults here as
  // belt-and-suspenders so a subsequent open never sees lingering edits
  // if the close path skipped forceClose (e.g. Drawer-internal close
  // when there were no unsaved changes).
  //
  // Guard on the open → closed transition using a ref so unrelated
  // re-renders while the drawer is already closed don't keep firing
  // form.reset — lineItemForReset is a prop whose reference churns on
  // every parent render, and without the transition guard we'd waste
  // work and risk masking real state bugs.
  const isOpen = props.isOpen;
  const lineItemForReset = props.lineItem;
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (isOpen || !wasOpen) return;
    setShowSnackbar(false);
    setShowUnsavedDialog(false);
    setIsCrossCheckExpanded(false);
    const defaults = getDefaultValues(
      lineItemForReset,
      defaultPricing,
      defaultCurrencyCode
    );
    form.reset(defaults);
    initialFormValues.current = defaults;
  }, [isOpen, lineItemForReset, defaultPricing, defaultCurrencyCode, form]);

  // Invalidate the project and charge queries whenever the drawer opens
  // so the data we render is fresh from the server — the parent project
  // layout route uses useSuspenseQuery with default settings, which
  // serves cached data across within-app navigation (e.g. audit tab →
  // line items tab). Without this, opening the drawer after an external
  // DB change (another user's edit, a manual reset during dev) would
  // show stale values from whatever the client cached when the project
  // was first loaded. Invalidating on open triggers a refetch; the
  // lineItem-sync effect below picks up the new values and resets the
  // form.
  const projectKey = props.projectKey;
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!isOpen) return;
    queryClient.invalidateQueries({ queryKey: ['project', projectKey] });
  }, [isOpen, projectKey, queryClient]);
  // Regenerate is disabled whenever any existing charge is INVOICED —
  // Regenerate is blocked outright whenever any existing charge is
  // INVOICED — invoice history depends on the row's exact shape, and a
  // schedule rebuild would force a delete + recreate the BE rejects.
  // Applies to both PERIODIC and MILESTONE schedules. BILLABLE is
  // intentionally NOT in the gate — mark-billable is user-reversible.
  const hasInvoicedCharge = useMemo(
    () => localCharges.some((charge) => charge.status === 'INVOICED'),
    [localCharges]
  );
  // An invoiced charge freezes this line item's currency and is the strongest
  // reason it is locked, so it is reported before the project-currency lock that
  // applies when another field already set the currency.
  const priceLock: IsoCurrencyCode | undefined = hasInvoicedCharge
    ? price.isoCode
    : (props.projectCurrencyLock ?? undefined);
  const priceLockTooltip = hasInvoicedCharge
    ? INVOICED_CHARGE_LOCKED_TOOLTIP
    : props.projectCurrencyLock
      ? projectCurrencyLockedTooltip(props.projectCurrencyLock)
      : undefined;
  // The total override is always locked to the line item price currency, so it
  // mirrors the price lock reason, falling back to "matches the line item price"
  // when the price selector itself is editable (sole currency source).
  const overrideCurrencyTooltip =
    priceLockTooltip ?? TOTAL_OVERRIDE_CURRENCY_TOOLTIP;
  // Activation date and charge mode lock once any charge has progressed
  // past PLANNED. BILLABLE counts here (unlike generate-blocking) because
  // moving the activation anchor would invalidate already-recognized
  // billable periods, even if the BE could still revert them.
  const hasLockedCharges = useMemo(
    () =>
      localCharges.some(
        (charge) => charge.status === 'INVOICED' || charge.status === 'BILLABLE'
      ),
    [localCharges]
  );
  const generateBlockedReason = useMemo(() => {
    if (hasInvoicedCharge) {
      return 'Cannot regenerate while any charge is invoiced';
    }
    if (chargeSchedule !== CHARGE_SCHEDULE.MILESTONE) return null;
    return milestoneGenerateBlockedReason(milestonePhases);
  }, [hasInvoicedCharge, chargeSchedule, milestonePhases]);
  const chargeListTotal = useMemo(() => {
    return sumChargeTotals(localCharges);
  }, [localCharges]);
  const chargeListInvoiceDateCount = useMemo(() => {
    const invoiceDates = new Set(
      localCharges
        .map((charge) => chargeDateKey(charge.expectedBillableDate))
        .filter((invoiceDate): invoiceDate is string => invoiceDate !== null)
    );
    return invoiceDates.size;
  }, [localCharges]);
  // Taint detection — actual value change, not just touched. Compare
  // day-strings so a DateTime object re-rendering the same date does not
  // show as tainted.
  const normalizedActivationDate = activationDateKey(activationDate);
  const initialActivationDate = initialFormValues.current.activationDate;
  const normalizedInitialActivationDate = activationDateKey(
    initialActivationDate
  );
  const isFormTainted =
    pricingMode !== initialFormValues.current.pricingMode ||
    priceAmount !== initialFormValues.current.price.amount ||
    price.isoCode !== initialFormValues.current.price.isoCode ||
    chargeSchedule !== initialFormValues.current.chargeSchedule ||
    chargeFrequency !== initialFormValues.current.chargeFrequency ||
    invoiceFrequency !== initialFormValues.current.invoiceFrequency ||
    invoiceMode !== initialFormValues.current.invoiceMode ||
    totalOverride.amount !== initialFormValues.current.totalOverride.amount ||
    chargeMode !== initialFormValues.current.chargeMode ||
    normalizedActivationDate !== normalizedInitialActivationDate;
  const hasUnsavedChanges = isFormTainted || chargesModified;

  const onClose = props.onClose;
  const lineItem = props.lineItem;

  const attemptClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const forceClose = useCallback(() => {
    setShowUnsavedDialog(false);
    form.reset(getDefaultValues(lineItem, defaultPricing, defaultCurrencyCode));
    onClose();
  }, [form, lineItem, defaultPricing, defaultCurrencyCode, onClose]);

  const handleGenerateChargesWithValidation = useCallback(() => {
    if (
      chargeSchedule === CHARGE_SCHEDULE.PERIODIC &&
      chargeMode === 'CONFIRMED' &&
      activationDateKey(form.getValues('activationDate')) === null
    ) {
      form.setError('activationDate', {
        type: 'client',
        message: 'Activation date required',
      });
      return;
    }
    handleGenerateCharges();
  }, [chargeSchedule, chargeMode, form, handleGenerateCharges]);

  const updateMutation = useUpdateProjectLineItem(props.projectKey, {
    additionalInvalidations: props.additionalInvalidations,
    onSuccess: () => {
      initialFormValues.current = form.getValues();
      chargeScheduleState.resetChargesModified();
      // Clear react-hook-form's dirty flag now that the current values are
      // the source of truth. Without this, the lineItem-sync effect's
      // dirty-guard would keep blocking future refetches from syncing in.
      form.reset(form.getValues());
      props.onClose();
    },
    onError: (_error, apiError) => {
      if (apiError.apiStatusCode === ERROR_CODE_MAPPING.plan_limit_exceeded) {
        setShowSnackbar(false);
        return;
      }

      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.billing_plan_activation_date_out_of_range
      ) {
        form.setError('activationDate', {
          type: 'manual',
          message: apiError.message,
        });
        setShowSnackbar(false);
        return;
      }
      // A mixed-currency rejection renders inline on the price field's
      // currency selector; mirrors the project discount / contract value
      // controls.
      if (apiError.errorType === 'project_mixed_currency') {
        form.setError('price.isoCode', {
          type: 'server',
          message: getUserFacingErrorMessage(apiError),
        });
        setShowSnackbar(false);
        return;
      }
      setShowSnackbar(true);
    },
  });

  const handleSubmit = form.handleSubmit((values) => {
    if (!props.lineItem) return;

    // Bound check: planned total and every charge in the schedule must
    // fit the NUMERIC(14,2) cap. The error is surfaced inline beneath
    // the line item total via `boundError` below — block save here
    // when that helper is visible, and scroll it into view so the user
    // sees why the click did nothing.
    if (boundError !== null) {
      boundErrorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }

    // Save intentionally not gated by isConfigOutOfSync — per the
    // billing-conceptual-model spec, config changes never auto-update
    // charges; the warning is the signal, Regenerate is the user's
    // call.
    const payload = buildLineItemUpdatePayload({
      values,
      stored: props.lineItem,
      hasDates,
      chargesModified,
      localCharges,
    });

    updateMutation.mutate({
      projectKey: props.projectKey,
      lineItemKey: props.lineItem.key,
      ...payload,
    });
  });

  return (
    <>
      <Drawer
        anchor="right"
        open={props.isOpen}
        onClose={attemptClose}
        sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}
        PaperProps={{ sx: { width: DRAWER_WIDTH } }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <BillingPlanDrawerHeader
            lineItem={props.lineItem}
            onClose={attemptClose}
          />

          {/* When the parent is still resolving the line item, skip
              the form entirely and render a skeleton body inside the
              same Drawer instance. Keeps the open transition stable
              (no Drawer remount when data arrives) so callers that
              fetch lazily don't flicker. */}
          {props.isLoading || props.lineItem === null ? (
            <BillingPlanDrawerSkeleton />
          ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <BillingPlanPricingSection
                control={form.control}
                pricingMode={pricingMode}
                price={priceAmount}
                quantity={quantity}
                chargeAmount={baseChargeAmount}
                currencyCode={price.isoCode}
                lockTo={priceLock}
                disabledTooltip={priceLockTooltip}
                defaultPrice={defaultPriceForMode}
              />

              <BillingPlanChargeScheduleSection
                control={form.control}
                setValue={form.setValue}
                hasDates={hasDates}
                chargeSchedule={chargeSchedule}
                chargeFrequency={chargeFrequency}
                chargeAmountRange={scheduleChargeRange}
                numCharges={numCharges}
                lineItemTotal={lineItemTotal}
                computedTotal={computedTotal}
                totalOverrideAmount={totalOverrideAmount}
                totalOverride={totalOverride}
                milestonePhases={milestonePhases}
                currencyCode={price.isoCode}
                currencyLockedTooltip={overrideCurrencyTooltip}
                isScheduleTypeLocked={hasInvoicedCharge}
                boundError={boundError}
                boundErrorRef={boundErrorRef}
              />

              <BillingPlanInvoiceScheduleSection
                control={form.control}
                hasDates={hasDates}
                isMilestone={chargeSchedule === CHARGE_SCHEDULE.MILESTONE}
                isFrequencyPairValid={isValidFrequencyPair(
                  chargeFrequency,
                  invoiceFrequency
                )}
                chargeFrequency={chargeFrequency}
                invoiceFrequency={invoiceFrequency}
                lineItemTotal={totalMoneyAcrossCharges}
                numInvoices={numInvoices}
                estInvoiceRange={estInvoiceRange}
                currencyCode={price.isoCode}
              />

              <BillingPlanActivationSection
                control={form.control}
                hasDates={hasDates}
                hasLockedCharges={hasLockedCharges}
                isMilestone={chargeSchedule === CHARGE_SCHEDULE.MILESTONE}
                errors={form.formState.errors}
                clearErrors={form.clearErrors}
              />

              {hasDates && (
                <BillingPlanChargeListSection
                  key={generationKey}
                  charges={localCharges}
                  isLoading={!chargesLoaded && !chargesModified}
                  pricingMode={pricingMode}
                  lineItemQuantity={quantity}
                  currencyCode={price.isoCode}
                  isFrequencyPairValid={isValidFrequencyPair(
                    chargeFrequency,
                    invoiceFrequency
                  )}
                  generateBlockedReason={generateBlockedReason}
                  isMilestoneMode={isMilestoneMode}
                  focusChargeKey={props.focusChargeKey}
                  onFocusChargeComplete={props.onFocusChargeComplete}
                  onGenerate={handleGenerateChargesWithValidation}
                  onEditingChange={setIsChargeEditing}
                  onUpdateCharge={handleUpdateCharge}
                  onDeleteCharge={handleDeleteCharge}
                  onSplitCharge={handleSplitCharge}
                  onAddCharge={handleAddCharge}
                />
              )}
            </Box>
          )}

          {/* Sticky footer */}
          <Box
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <BillingPlanReadinessSection
              billingPlanTotal={totalMoneyAcrossCharges}
              billingPlanChargeCount={numCharges}
              billingPlanInvoiceDateCount={
                // PENDING plans haven't been activated yet, so charges
                // carry no expected bill dates. The plan-side computed
                // total is 0 — any charge in the list with a non-null
                // bill date is drift and must surface as a cross-check
                // mismatch.
                chargeMode === 'PENDING'
                  ? 0
                  : isMilestoneMode
                    ? chargeListInvoiceDateCount
                    : numInvoices
              }
              chargeListTotal={chargeListTotal}
              chargeListChargeCount={localCharges.length}
              chargeListInvoiceDateCount={chargeListInvoiceDateCount}
              hasGeneratedCharges={localCharges.length > 0}
              isConfigOutOfSync={isConfigOutOfSync}
              isExpanded={isCrossCheckExpanded}
              onToggle={() => {
                setIsCrossCheckExpanded((prev) => !prev);
              }}
              currencyCode={price.isoCode}
            />
            <Box
              sx={{
                px: 2,
                py: 0.75,
                minHeight: 48,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Button variant="text" onClick={attemptClose} sx={{ px: 0 }}>
                Cancel
              </Button>
              <Tooltip
                arrow
                title={
                  isChargeEditing
                    ? 'Save or cancel the open charge edit first'
                    : !chargesLoaded && hasDates
                      ? 'Loading charges — save unlocks once they finish'
                      : ''
                }
              >
                <span>
                  <LoadingButton
                    variant="contained"
                    loading={updateMutation.isPending}
                    disabled={
                      props.isLoading === true ||
                      props.lineItem === null ||
                      isChargeEditing ||
                      (!chargesLoaded && hasDates)
                    }
                    onClick={handleSubmit}
                  >
                    Save plan
                  </LoadingButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <UnsavedChangesDialog
        isOpen={showUnsavedDialog}
        onCancel={() => setShowUnsavedDialog(false)}
        onProceed={forceClose}
      />

      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          updateMutation.reset();
        }}
        message="An error occurred saving billing configuration"
      />
    </>
  );
}
